/**
 * LA ZEBRA BRANCHÉE SUR CE POSTE — impression depuis le navigateur (11/10/2026).
 * ───────────────────────────────────────────────────────────────────────────
 *
 * « La Zebra sera installée sur l'ordinateur qui éditera les étiquettes. »
 * — RQ, 11/10/2026. Une imprimante USB n'a pas d'adresse réseau : le moteur ne
 * peut pas lui parler. C'est donc L'ÉCRAN qui imprime, par l'agent **Zebra
 * Browser Print** installé sur ce poste : un petit service local qui écoute sur
 * `localhost` et reçoit le ZPL que le moteur a préparé.
 *
 * ── Comment ça marche ──────────────────────────────────────────────────────
 *  · L'agent expose `http://localhost:9100` (page en http) et
 *    `https://localhost:9101` (page en https — la production). À la première
 *    demande, il affiche une fenêtre sur le poste pour ACCEPTER le site : c'est
 *    la seule installation à faire, une fois par poste.
 *  · `GET /default?type=printer` rend l'imprimante par défaut de l'agent ;
 *    `GET /available` toutes celles qu'il voit ; `POST /write` imprime
 *    `{ device, data }`.
 *
 * ── Ce que ce module ne fait pas ───────────────────────────────────────────
 *  · Il ne FABRIQUE pas le ZPL : il vient du moteur, le même que pour l'envoi
 *    réseau et le fichier — une seule étiquette, trois chemins.
 *  · Il ne retombe sur rien en silence : agent absent, site non accepté,
 *    aucune imprimante — chaque cas est dit, avec ce qu'il faut faire.
 */

export interface ImprimantePoste { name: string; uid: string; connection: string; deviceType: string; manufacturer?: string }
export type Detection = { ok: true; base: string; imprimante: ImprimantePoste } | { ok: false; raison: string; installer: boolean };

/** Lien d'installation de l'agent (gratuit, Windows et macOS). */
export const LIEN_BROWSER_PRINT = 'https://www.zebra.com/us/en/support-downloads/software/printer-software/browser-print.html';

/** Les adresses de l'agent à essayer, selon le protocole de la page : un site
 *  en https ne peut appeler que l'agent en https (contenu mixte refusé). */
function bases(): string[] {
  if (typeof window === 'undefined') return [];
  return window.location.protocol === 'https:' ? ['https://localhost:9101'] : ['http://localhost:9100', 'https://localhost:9101'];
}

async function lire(url: string, delaiMs = 2500): Promise<Response> {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), delaiMs);
  try { return await fetch(url, { signal: ctl.signal }); } finally { clearTimeout(t); }
}

/** L'imprimante par défaut de l'agent sur ce poste, ou la raison en clair. */
export async function imprimanteDuPoste(): Promise<Detection> {
  let agentVu = false;
  for (const base of bases()) {
    try {
      const r = await lire(`${base}/default?type=printer`);
      agentVu = true;
      if (r.status === 403 || r.status === 401) return { ok: false, installer: false, raison: 'Zebra Browser Print a refusé ce site : ouvrez l’icône de l’agent sur ce poste, et acceptez l’adresse de la Qualité.' };
      const texte = await r.text();
      if (!r.ok || !texte.trim()) continue;
      const d = JSON.parse(texte) as Partial<ImprimantePoste>;
      if (d && d.uid) return { ok: true, base, imprimante: d as ImprimantePoste };
    } catch { /* agent absent sur cette base, ou site non accepté (CORS) */ }
  }
  if (agentVu) return { ok: false, installer: false, raison: 'Zebra Browser Print tourne sur ce poste, mais ne voit aucune imprimante : branchez la Zebra, puis dans l’agent choisissez-la comme imprimante par défaut.' };
  return { ok: false, installer: true, raison: `Zebra Browser Print n’est pas joignable sur ce poste${window.location.protocol === 'https:' ? ' (en https, l’agent doit être accepté une fois sur https://localhost:9101)' : ''}. Installez-le sur l’ordinateur qui édite les étiquettes, puis acceptez ce site.` };
}

/** Envoie le ZPL à l'imprimante du poste. */
export async function imprimerSurLePoste(zpl: string): Promise<{ ok: true; imprimante: string } | { ok: false; raison: string; installer: boolean }> {
  const d = await imprimanteDuPoste();
  if (!d.ok) return d;
  try {
    const r = await fetch(`${d.base}/write`, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify({ device: d.imprimante, data: zpl }) });
    if (!r.ok) return { ok: false, installer: false, raison: `L’agent a refusé l’impression (HTTP ${r.status}).` };
    return { ok: true, imprimante: d.imprimante.name };
  } catch (e) {
    return { ok: false, installer: false, raison: `L’envoi à ${d.imprimante.name} a échoué : ${String((e as Error)?.message || e)}.` };
  }
}
